import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Shield, AlertTriangle, FileText, Users, Database, PieChart, Plus, Eye, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../layouts/AppLayout';

const DATA_CLASSIFICATIONS = [
  { value: 'PUBLICO', label: 'Público', color: 'bg-green-500' },
  { value: 'INTERNO', label: 'Interno', color: 'bg-blue-500' },
  { value: 'CONFIDENCIAL', label: 'Confidencial', color: 'bg-yellow-500' },
  { value: 'RESTRITO', label: 'Restrito', color: 'bg-orange-500' },
  { value: 'SENSIVEL', label: 'Sensível', color: 'bg-red-500' },
];

const DATA_CATEGORIES = [
  { value: 'IDENTIFICACAO', label: 'Identificação' },
  { value: 'FINANCEIRO', label: 'Financeiro' },
  { value: 'PROFISSIONAL', label: 'Profissional' },
  { value: 'SAUDE', label: 'Saúde' },
  { value: 'BIOMETRICO', label: 'Biométrico' },
  { value: 'JUDICIAL', label: 'Judicial' },
  { value: 'OUTRO', label: 'Outro' },
];

const LEGAL_BASIS = [
  { value: 'CONSENTIMENTO', label: 'Consentimento' },
  { value: 'OBRIGACAO_LEGAL', label: 'Obrigação Legal' },
  { value: 'EXECUCAO_POLITICAS', label: 'Execução de Políticas Públicas' },
  { value: 'EXECUCAO_CONTRATO', label: 'Execução de Contrato' },
  { value: 'INTERESSE_LEGITIMO', label: 'Interesse Legítimo' },
  { value: 'PROTECAO_VIDA', label: 'Proteção da Vida' },
  { value: 'TUTELA_SAUDE', label: 'Tutela da Saúde' },
];

const RETENTION_PERIODS = [
  { value: '6_MESES', label: '6 Meses' },
  { value: '1_ANO', label: '1 Ano' },
  { value: '2_ANOS', label: '2 Anos' },
  { value: '5_ANOS', label: '5 Anos' },
  { value: '10_ANOS', label: '10 Anos' },
  { value: '20_ANOS', label: '20 Anos' },
  { value: 'PERMANENTE', label: 'Permanente' },
];

export default function LGPDPage() {
  const { api } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMappingDialog, setShowMappingDialog] = useState(false);
  const [showIncidentDialog, setShowIncidentDialog] = useState(false);
  const [showRequestDialog, setShowRequestDialog] = useState(false);

  const [newMapping, setNewMapping] = useState({
    act_id: '',
    data_classification: 'PUBLICO',
    data_categories: ['IDENTIFICACAO'],
    legal_basis: 'OBRIGACAO_LEGAL',
    retention_period: '5_ANOS',
    purpose: '',
    data_subjects: '',
  });

  const [newIncident, setNewIncident] = useState({
    description: '',
    incident_type: 'Vazamento',
    affected_data_categories: ['IDENTIFICACAO'],
    estimated_affected_count: 0,
    detection_date: new Date().toISOString().split('T')[0],
    containment_actions: '',
    notification_required: false,
  });

  const [newRequest, setNewRequest] = useState({
    titular_cpf: '',
    titular_name: '',
    request_type: 'ACESSO',
    description: '',
    deadline: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [dashRes, mapRes, incRes, reqRes] = await Promise.all([
        api.get('/lgpd/dashboard'),
        api.get('/lgpd/data-mappings'),
        api.get('/lgpd/incidents'),
        api.get('/lgpd/titular-requests'),
      ]);
      setDashboard(dashRes.data);
      setMappings(mapRes.data.mappings || []);
      setIncidents(incRes.data.incidents || []);
      setRequests(reqRes.data.requests || []);
    } catch (error) {
      toast.error('Erro ao carregar dados LGPD');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMapping = async () => {
    try {
      await api.post('/lgpd/data-mappings', newMapping);
      toast.success('Mapeamento criado com sucesso');
      setShowMappingDialog(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao criar mapeamento');
    }
  };

  const handleCreateIncident = async () => {
    try {
      await api.post('/lgpd/incidents', {
        ...newIncident,
        detection_date: new Date(newIncident.detection_date).toISOString(),
      });
      toast.success('Incidente registrado com sucesso');
      setShowIncidentDialog(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao registrar incidente');
    }
  };

  const handleCreateRequest = async () => {
    try {
      await api.post('/lgpd/titular-requests', {
        ...newRequest,
        deadline: new Date(newRequest.deadline).toISOString(),
      });
      toast.success('Solicitação registrada com sucesso');
      setShowRequestDialog(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao registrar solicitação');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    if (score >= 40) return 'text-orange-500';
    return 'text-red-500';
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
    <div className="space-y-6" data-testid="lgpd-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-600" />
            LGPD & Proteção de Dados
          </h1>
          <p className="text-slate-500">Gestão de conformidade com a Lei Geral de Proteção de Dados</p>
        </div>
      </div>

      {/* Score e Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Score de Conformidade</p>
                <p className={`text-3xl font-bold ${getScoreColor(dashboard?.compliance_score || 0)}`}>
                  {dashboard?.compliance_score || 0}%
                </p>
              </div>
              <Shield className="h-10 w-10 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Mapeamentos</p>
                <p className="text-2xl font-bold text-slate-900">{dashboard?.total_mappings || 0}</p>
              </div>
              <Database className="h-8 w-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Incidentes Abertos</p>
                <p className="text-2xl font-bold text-red-600">{dashboard?.incidents?.open || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Solicitações Pendentes</p>
                <p className="text-2xl font-bold text-orange-600">{dashboard?.titular_requests?.pending || 0}</p>
              </div>
              <Users className="h-8 w-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recomendações */}
      {dashboard?.recommendations?.length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-yellow-800">
              <AlertTriangle className="h-5 w-5" />
              Recomendações
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1">
              {dashboard.recommendations.map((rec, i) => (
                <li key={i} className="text-sm text-yellow-700 flex items-start gap-2">
                  <span>•</span>
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="mappings" className="space-y-4">
        <TabsList>
          <TabsTrigger value="mappings" data-testid="tab-mappings">
            <Database className="h-4 w-4 mr-2" />
            Mapeamentos
          </TabsTrigger>
          <TabsTrigger value="incidents" data-testid="tab-incidents">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Incidentes
          </TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">
            <Users className="h-4 w-4 mr-2" />
            Solicitações
          </TabsTrigger>
        </TabsList>

        {/* Mapeamentos de Dados */}
        <TabsContent value="mappings">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Mapeamento de Dados</CardTitle>
                <CardDescription>Inventário de dados pessoais tratados</CardDescription>
              </div>
              <Dialog open={showMappingDialog} onOpenChange={setShowMappingDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="btn-new-mapping">
                    <Plus className="h-4 w-4 mr-2" />
                    Novo Mapeamento
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Novo Mapeamento de Dados</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>ID do Ato</Label>
                        <Input
                          value={newMapping.act_id}
                          onChange={(e) => setNewMapping({ ...newMapping, act_id: e.target.value })}
                          placeholder="act_xxx"
                        />
                      </div>
                      <div>
                        <Label>Classificação</Label>
                        <Select
                          value={newMapping.data_classification}
                          onValueChange={(v) => setNewMapping({ ...newMapping, data_classification: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DATA_CLASSIFICATIONS.map((c) => (
                              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Base Legal</Label>
                        <Select
                          value={newMapping.legal_basis}
                          onValueChange={(v) => setNewMapping({ ...newMapping, legal_basis: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {LEGAL_BASIS.map((b) => (
                              <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Período de Retenção</Label>
                        <Select
                          value={newMapping.retention_period}
                          onValueChange={(v) => setNewMapping({ ...newMapping, retention_period: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {RETENTION_PERIODS.map((p) => (
                              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Finalidade do Tratamento</Label>
                      <Textarea
                        value={newMapping.purpose}
                        onChange={(e) => setNewMapping({ ...newMapping, purpose: e.target.value })}
                        placeholder="Descreva a finalidade do tratamento dos dados..."
                      />
                    </div>
                    <div>
                      <Label>Titulares dos Dados</Label>
                      <Input
                        value={newMapping.data_subjects}
                        onChange={(e) => setNewMapping({ ...newMapping, data_subjects: e.target.value })}
                        placeholder="Ex: Servidores públicos, Cidadãos..."
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowMappingDialog(false)}>Cancelar</Button>
                    <Button onClick={handleCreateMapping}>Salvar</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID Ato</TableHead>
                    <TableHead>Classificação</TableHead>
                    <TableHead>Base Legal</TableHead>
                    <TableHead>Retenção</TableHead>
                    <TableHead>Criado em</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mappings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                        Nenhum mapeamento cadastrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    mappings.map((m) => (
                      <TableRow key={m.mapping_id}>
                        <TableCell className="font-mono text-sm">{m.act_id}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={DATA_CLASSIFICATIONS.find(c => c.value === m.data_classification)?.color}>
                            {DATA_CLASSIFICATIONS.find(c => c.value === m.data_classification)?.label || m.data_classification}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{LEGAL_BASIS.find(b => b.value === m.legal_basis)?.label || m.legal_basis}</TableCell>
                        <TableCell className="text-sm">{RETENTION_PERIODS.find(p => p.value === m.retention_period)?.label || m.retention_period}</TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {new Date(m.created_at).toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Incidentes */}
        <TabsContent value="incidents">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Incidentes de Segurança</CardTitle>
                <CardDescription>Registro de incidentes envolvendo dados pessoais</CardDescription>
              </div>
              <Dialog open={showIncidentDialog} onOpenChange={setShowIncidentDialog}>
                <DialogTrigger asChild>
                  <Button variant="destructive" data-testid="btn-new-incident">
                    <Plus className="h-4 w-4 mr-2" />
                    Registrar Incidente
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Registrar Incidente de Segurança</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Tipo de Incidente</Label>
                        <Select
                          value={newIncident.incident_type}
                          onValueChange={(v) => setNewIncident({ ...newIncident, incident_type: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Vazamento">Vazamento</SelectItem>
                            <SelectItem value="Acesso não autorizado">Acesso não autorizado</SelectItem>
                            <SelectItem value="Perda">Perda de dados</SelectItem>
                            <SelectItem value="Ransomware">Ransomware</SelectItem>
                            <SelectItem value="Outro">Outro</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Data de Detecção</Label>
                        <Input
                          type="date"
                          value={newIncident.detection_date}
                          onChange={(e) => setNewIncident({ ...newIncident, detection_date: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Descrição do Incidente</Label>
                      <Textarea
                        value={newIncident.description}
                        onChange={(e) => setNewIncident({ ...newIncident, description: e.target.value })}
                        placeholder="Descreva o incidente em detalhes..."
                      />
                    </div>
                    <div>
                      <Label>Estimativa de Afetados</Label>
                      <Input
                        type="number"
                        value={newIncident.estimated_affected_count}
                        onChange={(e) => setNewIncident({ ...newIncident, estimated_affected_count: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div>
                      <Label>Ações de Contenção</Label>
                      <Textarea
                        value={newIncident.containment_actions}
                        onChange={(e) => setNewIncident({ ...newIncident, containment_actions: e.target.value })}
                        placeholder="Descreva as ações tomadas para conter o incidente..."
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowIncidentDialog(false)}>Cancelar</Button>
                    <Button variant="destructive" onClick={handleCreateIncident}>Registrar</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Afetados</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Data</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {incidents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                        <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                        Nenhum incidente registrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    incidents.map((inc) => (
                      <TableRow key={inc.incident_id}>
                        <TableCell className="font-mono text-sm">{inc.incident_id}</TableCell>
                        <TableCell>{inc.incident_type}</TableCell>
                        <TableCell className="max-w-xs truncate">{inc.description}</TableCell>
                        <TableCell>{inc.estimated_affected_count}</TableCell>
                        <TableCell>
                          <Badge variant={inc.status === 'RESOLVIDO' ? 'default' : 'destructive'}>
                            {inc.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {new Date(inc.detection_date).toLocaleDateString('pt-BR')}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Solicitações de Titulares */}
        <TabsContent value="requests">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Solicitações de Titulares</CardTitle>
                <CardDescription>Requisições de acesso, correção e exclusão de dados</CardDescription>
              </div>
              <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="btn-new-request">
                    <Plus className="h-4 w-4 mr-2" />
                    Nova Solicitação
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Registrar Solicitação de Titular</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>CPF do Titular</Label>
                        <Input
                          value={newRequest.titular_cpf}
                          onChange={(e) => setNewRequest({ ...newRequest, titular_cpf: e.target.value })}
                          placeholder="000.000.000-00"
                        />
                      </div>
                      <div>
                        <Label>Nome do Titular</Label>
                        <Input
                          value={newRequest.titular_name}
                          onChange={(e) => setNewRequest({ ...newRequest, titular_name: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Tipo de Solicitação</Label>
                        <Select
                          value={newRequest.request_type}
                          onValueChange={(v) => setNewRequest({ ...newRequest, request_type: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ACESSO">Acesso aos Dados</SelectItem>
                            <SelectItem value="CORRECAO">Correção</SelectItem>
                            <SelectItem value="EXCLUSAO">Exclusão</SelectItem>
                            <SelectItem value="PORTABILIDADE">Portabilidade</SelectItem>
                            <SelectItem value="REVOGACAO">Revogação de Consentimento</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Prazo</Label>
                        <Input
                          type="date"
                          value={newRequest.deadline}
                          onChange={(e) => setNewRequest({ ...newRequest, deadline: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Descrição</Label>
                      <Textarea
                        value={newRequest.description}
                        onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })}
                        placeholder="Descreva a solicitação..."
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowRequestDialog(false)}>Cancelar</Button>
                    <Button onClick={handleCreateRequest}>Registrar</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Titular</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Prazo</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                        Nenhuma solicitação registrada
                      </TableCell>
                    </TableRow>
                  ) : (
                    requests.map((req) => (
                      <TableRow key={req.request_id}>
                        <TableCell className="font-mono text-sm">{req.request_id}</TableCell>
                        <TableCell>{req.titular_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{req.request_type}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {new Date(req.deadline).toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell>
                          <Badge variant={req.status === 'ATENDIDO' ? 'default' : (req.status === 'PENDENTE' ? 'secondary' : 'destructive')}>
                            {req.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
    </AppLayout>
  );
}
